/* client/src/components/Inventory.tsx */
import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Box,
  Alert,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Tabs,
  Tab,
  IconButton,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';

import {
  TransferWithinAStation as TransferIcon,
  Warehouse as LocationIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { 
  apiGetInventory, 
  apiGetInventoryLocations,
  apiGetInventoryTransfers,
  apiCreateInventoryTransfer,
  apiUpdateTransferStatus,
  apiUpdateInventoryItem,
  apiCreateInventoryLocation,
  apiGetAllBusinessUnits,
  apiGetMetalRates,
  apiGetTotalInventory   
} from '../services/apiService';
import { binanceService } from '../services/binanceService';
import { formatCurrency } from '../utils/formatters';

interface InventoryItem {
  id: number;
  name: string;
  type: 'dgd' | 'non_dgd' | 'tt_bars';
  purity: number;
  weight: number;
  weight_unit: 'grams' | 'kilograms' | 'ounces';
  location_id: number;
  location_name: string;
  purchase_date: string;
  purchase_price: number;
  supplier: string;
  reference_number: string;
  status: 'in_stock' | 'sold' | 'transferred' | 'audit';
  notes: string;
  created_at: string;
  updated_at: string;
  business_unit: string;
  business_unit_id: number;
}

interface InventoryLocation {
  id: number;
  name: string;
  address: string;
  contact_person: string;
  phone: string;
  is_active: boolean;
}

interface InventoryTransfer {
  id: number;
  item_id: number;
  item_name: string;
  from_location_id: number;
  from_location_name: string;
  to_location_id: number;
  to_location_name: string;
  transfer_date: string;
  transfer_reason: string;
  transferred_by: number;
  transferred_by_name: string;
  received_by: number;
  received_by_name: string;
  status: 'pending' | 'in_transit' | 'completed' | 'cancelled';
}

interface BusinessUnit {
  id: number;
  name: string;
}

interface InventorySummaryData {
  unit: string;
  quantity: number; // in kg
  value: number;
  average_rate: number;
}

function Inventory() {
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [locations, setLocations] = useState<InventoryLocation[]>([]);
  const [transfers, setTransfers] = useState<InventoryTransfer[]>([]);
  const [businessUnits, setBusinessUnits] = useState<BusinessUnit[]>([]);
  const [metalRates, setMetalRates] = useState<any>(null);
  const [inventorySummary, setInventorySummary] = useState<InventorySummaryData[]>([]);
  const [totalInventoryWeight, setTotalInventoryWeight] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [transferOpen, setTransferOpen] = useState(false);
  const [locationOpen, setLocationOpen] = useState(false);
  const [goldRates, setGoldRates] = useState<{
    goldPerGram: number;
    goldPerOz: number;
    lastUpdated: string;
    source: string;
  } | null>(null);
  const [filters, setFilters] = useState({
    type: '',
    location_id: '',
    status: '',
    business_unit_id: ''
  });

  const [transferFormData, setTransferFormData] = useState({
    item_id: '',
    from_location_id: '',
    to_location_id: '',
    transfer_date: new Date().toISOString().split('T')[0],
    transfer_reason: ''
  });

  const [locationFormData, setLocationFormData] = useState({
    name: '',
    address: '',
    contact_person: '',
    phone: '',
    is_active: true
  });

// Function to get current gold rates using BinanceService (from Dashboard)
  const fetchCurrentGoldRates = async (): Promise<{
    goldPerGram: number;
    goldPerOz: number;
    lastUpdated: string;
    source: string;
  } | null> => {
    try {
      const goldPrice = await binanceService.getGoldPrice();
      
      const usdToAed = 3.67;
      const ozToGram = 31.1034768;
      
      const goldPerOz = goldPrice.price * usdToAed;
      const goldPerGram = goldPerOz / ozToGram;
      
      return {
        goldPerGram: parseFloat(goldPerGram.toFixed(2)),
        goldPerOz: parseFloat(goldPerOz.toFixed(2)),
        lastUpdated: new Date().toISOString(),
        source: 'Binance API'
      };
    } catch (error) {
      console.error('Error fetching current gold rates:', error);
      return null;
    }
  };

  // Simple function to get business unit name
  const getBusinessUnitForDisplay = (item: InventoryItem): string => {
    // First try to get from business_unit field
    if (item.business_unit) {
      return item.business_unit;
    }
    
    // If not available, look up by business_unit_id
    if (item.business_unit_id && businessUnits.length > 0) {
      const businessUnit = businessUnits.find(unit => unit.id === item.business_unit_id);
      return businessUnit ? businessUnit.name : 'Unassigned';
    }
    
    return 'Unassigned';
  };

  // Convert any weight to grams with proper type handling
  const convertToGrams = (weight: number, unit: string): number => {
    const numericWeight = Number(weight) || 0;
    
    if (unit === 'kilograms') return numericWeight * 1000;
    if (unit === 'ounces') return numericWeight * 28.3495;
    return numericWeight; // already in grams
  };

  // Convert any weight to kilograms with proper type handling
  const convertToKilograms = (weight: number, unit: string): number => {
    const numericWeight = Number(weight) || 0;
    
    if (unit === 'grams') return numericWeight / 1000;
    if (unit === 'ounces') return (numericWeight * 28.3495) / 1000;
    return numericWeight; // already in kilograms
  };

  // Calculate average rate per gram for all inventory
  const calculateAverageRatePerGram = (): number => {
    if (!items.length) return 0;
    
    const inStockItems = items.filter(item => item.status === 'in_stock');
    if (inStockItems.length === 0) return 0;
    
    let totalValue = 0;
    let totalWeightGrams = 0;
    
    inStockItems.forEach(item => {
      // Convert weight to grams and ensure it's a number
      const weightGrams = convertToGrams(Number(item.weight) || 0, item.weight_unit);
      const purchasePrice = Number(item.purchase_price) || 0;
      
      totalWeightGrams += weightGrams;
      totalValue += purchasePrice;
    });
    
    return totalWeightGrams > 0 ? totalValue / totalWeightGrams : 0;
  };

  // Calculate total weight in Kg for a business unit by name
  const calculateBusinessUnitWeight = (businessUnitName: string): number => {
    if (!items.length) return 0;
    
    const unitItems = items.filter(item => {
      const itemBusinessUnit = getBusinessUnitForDisplay(item);
      return itemBusinessUnit === businessUnitName && item.status === 'in_stock';
    });
    
    return unitItems.reduce((total: number, item: InventoryItem) => {
      return total + convertToKilograms(Number(item.weight) || 0, item.weight_unit);
    }, 0);
  };

  // Calculate average purchase price per gram for a business unit by name
  const calculateBusinessUnitAverageRateByName = (businessUnitName: string): number => {
    if (!items.length) return 0;
    
    const unitItems = items.filter(item => {
      const itemBusinessUnit = getBusinessUnitForDisplay(item);
      return itemBusinessUnit === businessUnitName && item.status === 'in_stock';
    });
    
    if (unitItems.length === 0) return 0;
    
    let totalValue = 0;
    let totalWeightGrams = 0;
    
    unitItems.forEach(item => {
      // Convert weight to grams and ensure numeric values
      const weightGrams = convertToGrams(Number(item.weight) || 0, item.weight_unit);
      const purchasePrice = Number(item.purchase_price) || 0;
      
      totalWeightGrams += weightGrams;
      totalValue += purchasePrice;
    });
    
    return totalWeightGrams > 0 ? totalValue / totalWeightGrams : 0;
  };

  // Calculate total value for a business unit by name
  const calculateBusinessUnitValue = (businessUnitName: string): number => {
    if (!items.length) return 0;
    
    const unitItems = items.filter(item => {
      const itemBusinessUnit = getBusinessUnitForDisplay(item);
      return itemBusinessUnit === businessUnitName && item.status === 'in_stock';
    });
    
    return unitItems.reduce((total, item) => {
      return total + (Number(item.purchase_price) || 0);
    }, 0);
  };

  const calculateLiveValue = (): number => {
  // Use goldRates instead of metalRates
  if (!goldRates?.goldPerGram) {
    console.log('No gold rates available for live value calculation');
    return 0;
  }
  
  const totalWeightGrams = items
    .filter(item => item.status === 'in_stock')
    .reduce((total: number, item: InventoryItem) => {
      return total + convertToGrams(Number(item.weight) || 0, item.weight_unit);
    }, 0);
  
  const goldPricePerGram = Number(goldRates.goldPerGram) || 0;
  return totalWeightGrams * goldPricePerGram;
};

  // Get all business units from the system
  const getAllBusinessUnits = (): string[] => {
    const allUnitsSet = new Set<string>();
    
    // Get units from items' business_unit field
    items.forEach(item => {
      if (item.business_unit && item.business_unit.trim() !== '') {
        allUnitsSet.add(item.business_unit);
      }
    });
    
    // Also include all business units from the system
    businessUnits.forEach(unit => {
      allUnitsSet.add(unit.name);
    });
    
    return Array.from(allUnitsSet);
  };

  // Get dashboard business units (all units from the system)
  const getDashboardBusinessUnits = (): string[] => {
    return businessUnits.map(unit => unit.name);
  };

  // Fetch metal rates from backend API
  useEffect(() => {
    const fetchMetalRates = async () => {
      try {
        const rates = await fetchCurrentGoldRates();
        if (rates) {
          setGoldRates(rates);
        } else {
          // Fallback to backend API if Binance fails
          const backendRates = await apiGetMetalRates();
          setGoldRates({
            goldPerGram: backendRates.goldPerGram,
            goldPerOz: backendRates.goldPerOz,
            lastUpdated: backendRates.lastUpdated,
            source: backendRates.source
          });
        }
      } catch (error) {
        console.error('Failed to fetch metal rates:', error);
        // Final fallback
        setGoldRates({
          goldPerGram: 240,
          goldPerOz: 7464.80, // 240 * 31.1035
          lastUpdated: new Date().toISOString(),
          source: 'fallback'
        });
      }
    };

    fetchMetalRates();
    const interval = setInterval(fetchMetalRates, 300000); // Refresh every 5 minutes
    return () => clearInterval(interval);
  }, []);

  // Updated function to fetch inventory summary with ACTUAL business transaction values
  const fetchInventorySummaryData = async () => {
    try {
      console.log('Fetching inventory summary for Inventory page...');
      const inventoryData = await apiGetTotalInventory();
      console.log('Inventory page API Response:', inventoryData);
      
      if (inventoryData && inventoryData.business_units) {
        // Use ACTUAL business transaction values from API (purchase prices)
        const summaryData = inventoryData.business_units.map((unit: any) => ({
          unit: unit.unit || 'Unknown',
          quantity: (unit.quantity || 0) / 1000, // Convert grams to kg for display
          value: unit.value || 0, // ACTUAL purchased value from business transactions
          average_rate: unit.average_rate || 0 // ACTUAL average purchase rate
        }));
        
        // Update the state variables
        setInventorySummary(summaryData);
        
        // Calculate total weight in kg from ACTUAL data
        const totalWeightKg = summaryData.reduce((total: number, item: any) => 
          total + (item.quantity || 0), 0);
        
        setTotalInventoryWeight(totalWeightKg);
        console.log('Total weight calculated in Inventory:', totalWeightKg);
        
      } else {
        console.error('Invalid inventory data structure in Inventory page:', inventoryData);
      }
    } catch (err) {
      console.error('Error fetching inventory data in Inventory page:', err);
    }
  };

  useEffect(() => {
    fetchInventory();
    fetchLocations();
    fetchTransfers();
    fetchBusinessUnits();
    fetchInventorySummaryData();
  }, [filters]);

  const fetchInventory = async () => {
    try {
      setLoading(true);
      const data = await apiGetInventory(filters);
      setItems(data);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch inventory');
    } finally {
      setLoading(false);
    }
  };

  const fetchBusinessUnits = async () => {
    try {
      const units = await apiGetAllBusinessUnits();
      setBusinessUnits(units);
    } catch (error) {
      console.error('Failed to fetch business units:', error);
      // Fallback to hardcoded units
      setBusinessUnits([
        { id: 1, name: 'Unit A' },
        { id: 2, name: 'Unit B' },
        { id: 3, name: 'Unit C' },
        { id: 4, name: 'Other' }
      ]);
    }
  };

  const fetchLocations = async () => {
    try {
      const data = await apiGetInventoryLocations();
      setLocations(data);
    } catch (err: any) {
      console.error('Failed to fetch locations:', err);
    }
  };

  const fetchTransfers = async () => {
    try {
      const data = await apiGetInventoryTransfers();
      setTransfers(data);
    } catch (err: any) {
      console.error('Failed to fetch transfers:', err);
    }
  };

  const handleTransferOpen = (item: InventoryItem) => {
    setTransferFormData({
      item_id: item.id.toString(),
      from_location_id: item.location_id.toString(),
      to_location_id: '',
      transfer_date: new Date().toISOString().split('T')[0],
      transfer_reason: ''
    });
    setTransferOpen(true);
  };

  const handleTransferClose = () => {
    setTransferOpen(false);
  };

  const handleLocationOpen = () => {
    setLocationFormData({
      name: '',
      address: '',
      contact_person: '',
      phone: '',
      is_active: true
    });
    setLocationOpen(true);
  };

  const handleLocationClose = () => {
    setLocationOpen(false);
  };

  const handleTransferSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await apiCreateInventoryTransfer(transferFormData);
      fetchTransfers();
      fetchInventory();
      handleTransferClose();
    } catch (err: any) {
      setError(err.message || 'Failed to create transfer');
    }
  };

  const handleLocationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await apiCreateInventoryLocation(locationFormData);
      fetchLocations();
      handleLocationClose();
    } catch (err: any) {
      setError(err.message || 'Failed to create location');
    }
  };

  const handleStatusUpdate = async (transferId: number, status: string) => {
    try {
      await apiUpdateTransferStatus(transferId, status);
      
      if (status === 'completed') {
        const transfer = transfers.find(t => t.id === transferId);
        if (transfer) {
          await apiUpdateInventoryItem(transfer.item_id, {
            status: 'in_stock',
            location_id: transfer.to_location_id
          });
        }
      }
      
      fetchTransfers();
      fetchInventory();
    } catch (err: any) {
      setError(err.message || 'Failed to update transfer status');
    }
  };

  const handleFilterChange = (filter: string, value: string) => {
    setFilters(prev => ({ ...prev, [filter]: value }));
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'dgd': return 'DGD Metal';
      case 'non_dgd': return 'Non-DGD Metal';
      case 'tt_bars': return 'TT Bars';
      default: return type;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'in_stock': return 'success';
      case 'sold': return 'error';
      case 'transferred': return 'warning';
      case 'audit': return 'info';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  const dashboardBusinessUnits = getDashboardBusinessUnits();
  const allBusinessUnits = getAllBusinessUnits();
  
  // Calculate total weight in Kg
  const totalWeightKg = items
    .filter(item => item.status === 'in_stock')
    .reduce((total: number, item: InventoryItem) => {
      return total + convertToKilograms(item.weight, item.weight_unit);
    }, 0);

  // Calculate total purchase value
  const totalPurchaseValue = items
    .filter(item => item.status === 'in_stock')
    .reduce((total: number, item: InventoryItem) => {
      return total + (item.purchase_price || 0);
    }, 0);

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Inventory Management</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<LocationIcon />} onClick={() => setTabValue(2)}>
            View Locations
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Total Weight in Kg */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Total Weight (Kg)
              </Typography>
              <Typography variant="h5" component="div" color="primary">
                {totalWeightKg.toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Total Value (Live Gold Price) */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Total Value (Live)
              </Typography>
              <Typography variant="h5" component="div" color="primary">
                د.{calculateLiveValue().toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Gold: د.{(goldRates?.goldPerGram || 0).toFixed(2)}/g
              </Typography>
              {metalRates?.source && (
                <Typography variant="caption" color="textSecondary" display="block">
                  Source: {metalRates.source}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Average Rate Per Gram (All Inventory) */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography color="textSecondary" gutterBottom variant="body2">
                Avg. Rate/Gram
              </Typography>
              <Typography variant="h5" component="div" color="primary">
                د.{calculateAverageRatePerGram().toFixed(2)}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Purchase Average
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Premium/Discount vs Live */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography color="textSecondary" gutterBottom variant="body2">
                vs Live Price
              </Typography>
              <Typography 
                variant="h5" 
                component="div" 
                color={
                  calculateAverageRatePerGram() > (metalRates?.goldPerGram || 0) 
                    ? 'error' 
                    : calculateAverageRatePerGram() < (metalRates?.goldPerGram || 0) 
                      ? 'success' 
                      : 'primary'
                }
              >
                {calculateAverageRatePerGram() > (metalRates?.goldPerGram || 0) ? '+' : ''}
                {(
                  ((calculateAverageRatePerGram() - (metalRates?.goldPerGram || 0)) / 
                  (metalRates?.goldPerGram || 1)) * 100
                ).toFixed(1)}%
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {calculateAverageRatePerGram() > (metalRates?.goldPerGram || 0) ? 'Premium' : 'Discount'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Business Unit Cards - Show ALL business units */}
      <Box sx={{ mt: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Business Unit Inventory Summary</Typography>
        <Grid container spacing={2}>
          {dashboardBusinessUnits.map((unitName) => {
            // Calculate values with proper type conversion
            const totalWeight = calculateBusinessUnitWeight(unitName);
            const avgRatePerGram = calculateBusinessUnitAverageRateByName(unitName);
            const totalValue = calculateBusinessUnitValue(unitName);

            return (
              <Grid item xs={12} sm={6} md={4} key={unitName}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {unitName}
                  </Typography>
                  
                  {/* Weight */}
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="h6" color="primary">
                      {totalWeight.toFixed(2)} Kg
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Total Weight
                    </Typography>
                  </Box>

                  {/* Average Rate */}
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="h6" color="secondary">
                      د.{avgRatePerGram.toFixed(2)}/g
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Average Rate
                    </Typography>
                  </Box>

                  {/* Total Value - FIXED FORMATTING */}
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="h6" color="primary">
                      د.{totalValue.toLocaleString('en-US', { 
                        minimumFractionDigits: 2, 
                        maximumFractionDigits: 2 
                      })}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Total Value
                    </Typography>
                  </Box>

                  {/* Premium/Discount Indicator */}
                  {metalRates?.goldPerGram && avgRatePerGram > 0 && (
                    <Box>
                      <Chip 
                        label={
                          avgRatePerGram > metalRates.goldPerGram 
                            ? `Premium: +${((avgRatePerGram - metalRates.goldPerGram) / metalRates.goldPerGram * 100).toFixed(1)}%` 
                            : `Discount: -${((metalRates.goldPerGram - avgRatePerGram) / metalRates.goldPerGram * 100).toFixed(1)}%`
                        }
                        color={
                          avgRatePerGram > metalRates.goldPerGram 
                            ? 'error' 
                            : avgRatePerGram < metalRates.goldPerGram 
                              ? 'success' 
                              : 'default'
                        }
                        size="small"
                      />
                    </Box>
                  )}
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      </Box>


      <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ mb: 3 }}>
        <Tab label="Inventory Items" />
        <Tab label="Transfers" />
        <Tab label="Locations" />
      </Tabs>

      {tabValue === 0 && (
        <>
          {/* Filters */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={filters.type}
                  label="Type"
                  onChange={(e) => handleFilterChange('type', e.target.value)}
                >
                  <MenuItem value="">All Types</MenuItem>
                  <MenuItem value="dgd">DGD Metal</MenuItem>
                  <MenuItem value="non_dgd">Non-DGD Metal</MenuItem>
                  <MenuItem value="tt_bars">TT Bars</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth>
                <InputLabel>Location</InputLabel>
                <Select
                  value={filters.location_id}
                  label="Location"
                  onChange={(e) => handleFilterChange('location_id', e.target.value)}
                >
                  <MenuItem value="">All Locations</MenuItem>
                  {locations.map(location => (
                    <MenuItem key={location.id} value={location.id}>
                      {location.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  label="Status"
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="in_stock">In Stock</MenuItem>
                  <MenuItem value="sold">Sold</MenuItem>
                  <MenuItem value="transferred">Transferred</MenuItem>
                  <MenuItem value="audit">Audit</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth>
                <InputLabel>Business Unit</InputLabel>
                <Select
                  value={filters.business_unit_id}
                  label="Business Unit"
                  onChange={(e) => handleFilterChange('business_unit_id', e.target.value)}
                >
                  <MenuItem value="">All Business Units</MenuItem>
                  {businessUnits.map(unit => (
                    <MenuItem key={unit.id} value={unit.id}>
                      {unit.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Business Unit</TableCell>
                  <TableCell>Weight</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Purchase Price</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>{item.name}</TableCell>
                    <TableCell>
                      <Chip 
                        label={getTypeLabel(item.type)} 
                        color="primary" 
                        size="small" 
                      />
                    </TableCell>
                    <TableCell>
                      {getBusinessUnitForDisplay(item)}
                    </TableCell>
                    <TableCell>
                      {item.weight.toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} {item.weight_unit}
                    </TableCell>
                    <TableCell>{item.location_name}</TableCell>
                    <TableCell>
                      د.{
                        item.purchase_price 
                          ? typeof item.purchase_price === 'number' 
                            ? item.purchase_price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                            : Number(item.purchase_price).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                          : '0.00'
                      }
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={item.status.replace('_', ' ')} 
                        color={getStatusColor(item.status) as any} 
                        size="small" 
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton 
                        size="small" 
                        onClick={() => handleTransferOpen(item)}
                        disabled={item.status !== 'in_stock'}
                        title={item.status !== 'in_stock' ? 'Item not available for transfer' : 'Transfer Item'}
                      >
                        <TransferIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {items.length === 0 && !loading && (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="textSecondary">No inventory items found</Typography>
              </Box>
            )}
          </TableContainer>
        </>
      )}

      {tabValue === 1 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Item</TableCell>
                <TableCell>From Location</TableCell>
                <TableCell>To Location</TableCell>
                <TableCell>Transfer Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {transfers.map((transfer) => (
                <TableRow key={transfer.id}>
                  <TableCell>{transfer.item_name}</TableCell>
                  <TableCell>{transfer.from_location_name}</TableCell>
                  <TableCell>{transfer.to_location_name}</TableCell>
                  <TableCell>{new Date(transfer.transfer_date).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Chip 
                      label={transfer.status.replace('_', ' ')} 
                      color={
                        transfer.status === 'completed' ? 'success' :
                        transfer.status === 'cancelled' ? 'error' :
                        transfer.status === 'in_transit' ? 'warning' : 'default'
                      } 
                      size="small" 
                    />
                  </TableCell>
                  <TableCell>
                    {transfer.status === 'pending' && (
                      <>
                        <Button 
                          size="small" 
                          onClick={() => handleStatusUpdate(transfer.id, 'in_transit')}
                          sx={{ mr: 1 }}
                        >
                          Start Transfer
                        </Button>
                        <Button 
                          size="small" 
                          color="error"
                          onClick={() => handleStatusUpdate(transfer.id, 'cancelled')}
                        >
                          Cancel
                        </Button>
                      </>
                    )}
                    {transfer.status === 'in_transit' && (
                      <Button 
                        size="small" 
                        onClick={() => handleStatusUpdate(transfer.id, 'completed')}
                      >
                        Complete
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {transfers.length === 0 && (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="textSecondary">No transfers found</Typography>
            </Box>
          )}
        </TableContainer>
      )}

      {tabValue === 2 && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button 
              variant="contained" 
              startIcon={<AddIcon />}
              onClick={handleLocationOpen}
            >
              Add Location
            </Button>
          </Box>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Address</TableCell>
                  <TableCell>Contact Person</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {locations.map((location) => (
                  <TableRow key={location.id}>
                    <TableCell>{location.name}</TableCell>
                    <TableCell>{location.address}</TableCell>
                    <TableCell>{location.contact_person}</TableCell>
                    <TableCell>{location.phone}</TableCell>
                    <TableCell>
                      <Chip 
                        label={location.is_active ? 'Active' : 'Inactive'} 
                        color={location.is_active ? 'success' : 'error'} 
                        size="small" 
                      />
                  </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {locations.length === 0 && (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="textSecondary">No locations found</Typography>
              </Box>
            )}
          </TableContainer>
        </>
      )}

      {/* Transfer Dialog */}
      <Dialog open={transferOpen} onClose={handleTransferClose} maxWidth="sm" fullWidth>
        <DialogTitle>Transfer Inventory Item</DialogTitle>
        <form onSubmit={handleTransferSubmit}>
          <DialogContent>
            <TextField
              margin="dense"
              name="from_location_id"
              label="From Location"
              fullWidth
              variant="outlined"
              value={locations.find(l => l.id.toString() === transferFormData.from_location_id)?.name || ''}
              InputProps={{ readOnly: true }}
            />
            <FormControl fullWidth margin="dense">
              <InputLabel>To Location</InputLabel>
              <Select
                name="to_location_id"
                value={transferFormData.to_location_id}
                label="To Location"
                onChange={(e) => setTransferFormData({...transferFormData, to_location_id: e.target.value})}
                required
              >
                <MenuItem value="">Select Location</MenuItem>
                {locations.filter(l => l.id.toString() !== transferFormData.from_location_id).map(location => (
                  <MenuItem key={location.id} value={location.id}>
                    {location.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              margin="dense"
              name="transfer_date"
              label="Transfer Date"
              type="date"
              fullWidth
              variant="outlined"
              value={transferFormData.transfer_date}
              onChange={(e) => setTransferFormData({...transferFormData, transfer_date: e.target.value})}
              InputLabelProps={{ shrink: true }}
              required
            />
            <TextField
              margin="dense"
              name="transfer_reason"
              label="Transfer Reason"
              fullWidth
              variant="outlined"
              value={transferFormData.transfer_reason}
              onChange={(e) => setTransferFormData({...transferFormData, transfer_reason: e.target.value})}
              multiline
              rows={2}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleTransferClose}>Cancel</Button>
            <Button type="submit" variant="contained">Create Transfer</Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Location Creation Dialog */}
      <Dialog open={locationOpen} onClose={handleLocationClose} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Location</DialogTitle>
        <form onSubmit={handleLocationSubmit}>
          <DialogContent>
            <TextField
              margin="dense"
              name="name"
              label="Location Name"
              fullWidth
              variant="outlined"
              value={locationFormData.name}
              onChange={(e) => setLocationFormData({...locationFormData, name: e.target.value})}
              required
            />
            <TextField
              margin="dense"
              name="address"
              label="Address"
              fullWidth
              variant="outlined"
              value={locationFormData.address}
              onChange={(e) => setLocationFormData({...locationFormData, address: e.target.value})}
              multiline
              rows={2}
            />
            <TextField
              margin="dense"
              name="contact_person"
              label="Contact Person"
              fullWidth
              variant="outlined"
              value={locationFormData.contact_person}
              onChange={(e) => setLocationFormData({...locationFormData, contact_person: e.target.value})}
            />
            <TextField
              margin="dense"
              name="phone"
              label="Phone"
              fullWidth
              variant="outlined"
              value={locationFormData.phone}
              onChange={(e) => setLocationFormData({...locationFormData, phone: e.target.value})}
            />
            <FormControl fullWidth margin="dense">
              <InputLabel>Status</InputLabel>
              <Select
                name="is_active"
                value={locationFormData.is_active ? 1 : 0}
                label="Status"
                onChange={(e) => setLocationFormData({...locationFormData, is_active: e.target.value === 1})}
              >
                <MenuItem value={1}>Active</MenuItem>
                <MenuItem value={0}>Inactive</MenuItem>
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleLocationClose}>Cancel</Button>
            <Button type="submit" variant="contained">Create Location</Button>
          </DialogActions>
        </form>
      </Dialog>
    </Container>
  );
}

export default Inventory;